import { useLayoutEffect, useRef, useState, useEffect, ReactNode } from 'react';
import { Box, Tooltip, useTheme } from '@mui/material';

import BoxLink from '@watershed/ui-core/components/BoxLink';
import { Analytics } from '@watershed/analytics/analyticsUtils';
import { getPaletteUtils, mixinSx, sxType } from '@watershed/style/styleUtils';
import { transition, NavItemData, NavSectionData } from './utils';
import { smartLowerCase } from '@watershed/shared-universal/utils/helpers';
import NavChip from './NavChip';
import { getSharedSidebarStyles } from './variants/sharedSidebarStyles';
import MaybeTooltip from '@watershed/ui-core/utils/MaybeTooltip';
import AddIcon from '@watershed/icons/components/Add';
import ChevronDownIcon from '@watershed/icons/components/ChevronDown';
import ChevronUpIcon from '@watershed/icons/components/ChevronUp';

function isActiveNavLink(
  entry: NavItemData,
  activeEntry: NavItemData | NavSectionData | null
) {
  return entry === activeEntry;
}

function useSx() {
  const theme = useTheme();
  const paletteUtils = getPaletteUtils(theme.palette);
  const { COLORS } = getSharedSidebarStyles(theme);
  return {
    navItemMain: sxType({
      textDecoration: 'none',
      border: 'none',
      borderLeft: '4px solid',
      borderColor: 'transparent',
      outlineColor: theme.palette.primary.main,
      fontWeight: 600,
      padding: '0 10px',
      height: '28px',
      lineHeight: '28px',
      display: 'flex',
      alignItems: 'center',
      transition: transition(['background-color', 72]),

      backgroundColor: 'transparent',
      color: COLORS.TEXT_PRIMARY,
      width: '100%',

      '&:focus': {
        boxShadow: paletteUtils.boxShadowFocus,
        outline: 'none',
      },
      '&:hover': {
        backgroundColor: COLORS.BACKGROUND_ACTIVE,
      },
      '&:active': {
        backgroundColor: COLORS.BACKGROUND_ACTIVE,
      },
      '&[aria-disabled="true"]': {
        backgroundColor: 'initial !important',
        color: COLORS.TEXT_DISABLED,
        cursor: 'default',
      },
    }),
    navItemMainCollapsedWithActiveSubentry: sxType({
      borderColor: COLORS.BORDER_ACTIVE,
    }),
    navItemMainExpandable: sxType({
      marginRight: 4,
      marginTop: 2,
      transitionProperty: 'transform',
      transitionDuration: '0.2s',
      transitionTimingFunction: 'ease-in-out',
    }),
    navItemMainExpanded: sxType({
      transform: 'rotate(90deg) translate(-2px, 0)',
    }),
    navItem: sxType({
      position: 'relative',
      textDecoration: 'none',
      outlineColor: theme.palette.primary.main,
      paddingLeft: '42px',
      paddingRight: '4px',
      height: '28px',
      lineHeight: '28px',
      display: 'flex',
      alignItems: 'center',
      borderLeft: '4px solid',
      borderColor: 'transparent',
      transition: transition(['background-color', 72], ['color', 72]),
      color: COLORS.TEXT_SECONDARY,
      outline: 'none',

      '& > span': {
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },

      '&[aria-disabled="true"]': {
        background: 'none',
        color: COLORS.TEXT_DISABLED,
        cursor: 'default',
      },

      '&:not([aria-disabled="true"])': {
        '&:focus': {
          backgroundColor: COLORS.BACKGROUND_ACTIVE,
          boxShadow: paletteUtils.boxShadowFocus,
        },
        '&:hover': {
          backgroundColor: COLORS.BACKGROUND_ACTIVE,
        },
        '&:active': {
          backgroundColor: COLORS.BACKGROUND_ACTIVE,
          // Darken on click by 15% (down to 85%)
          // TODO: If we keep this, use a real color.
          filter: 'brightness(0.85)',
        },
      },
    }),
    navItemActive: sxType({
      color: COLORS.TEXT_PRIMARY,
      backgroundColor: COLORS.BACKGROUND_ACTIVE,
      borderColor: COLORS.BORDER_ACTIVE,
    }),
    navItemLevelTwo: sxType({
      paddingLeft: '56px',
    }),
    permissionsTooltip: sxType({
      left: theme.spacing(-2),
    }),
  };
}

function LinkComponent({
  section,
  activeEntry,
  hasPermission,
  children,
  sx,
  appearsCollapsed,
  isNavItem,
}: {
  section: NavSectionData;
  activeEntry: NavSectionData | NavItemData | null;
  children: ReactNode;
  hasPermission: boolean;
  sx: ReturnType<typeof useSx>;
  appearsCollapsed: boolean;
  isNavItem?: boolean;
}) {
  const hasActiveSubentry = section.subentries.some(
    (sub) => sub === activeEntry
  );
  const disabledSpanSx = mixinSx(
    sx.navItemMain,
    hasActiveSubentry &&
      appearsCollapsed &&
      sx.navItemMainCollapsedWithActiveSubentry
  );
  const disabledSpan = (
    <Box component="span" sx={disabledSpanSx} aria-disabled>
      {children}
    </Box>
  );
  return section.disabled ? (
    <MaybeTooltip title={section.tooltip} placement="right-start">
      {disabledSpan}
    </MaybeTooltip>
  ) : !hasPermission ? (
    <Tooltip
      title={`Your permissions don’t allow for accessing ${smartLowerCase(
        section.name
      )}.`}
      placement="right"
      sx={sx.permissionsTooltip}
    >
      {disabledSpan}
    </Tooltip>
  ) : (
    <Tooltip title={section.tooltip || ''} placement="right-start">
      <BoxLink
        sx={mixinSx(
          disabledSpanSx,
          isNavItem && sx.navItem,
          isNavItem && { paddingLeft: '10px' },
          isActiveNavLink(section, activeEntry) && sx.navItemActive
        )}
        href={section.location}
        newTab={section.openInNewTab}
        onClick={() => {
          Analytics.action('navBarTab', {
            label: section.name,
            link:
              section.location.pathname +
              (section.location.search || '') +
              (section.location.hash ? `#${section.location.hash}` : ''),
          });
        }}
      >
        {children}
      </BoxLink>
    </Tooltip>
  );
}

export type NavSectionProps = {
  section: NavSectionData;
  activeEntry: NavSectionData | NavItemData | null;
  appearsCollapsed: boolean;
};

function hasActiveSubentryRecursive(
  section: NavSectionData | NavItemData,
  activeEntry: NavSectionData | NavItemData | null
): boolean {
  return (
    section.subentries?.some(
      (sub) =>
        sub === activeEntry || hasActiveSubentryRecursive(sub, activeEntry)
    ) ?? false
  );
}

export default function NavSection({
  section,
  appearsCollapsed,
  activeEntry,
}: NavSectionProps) {
  const sx = useSx();
  const showSubentries = section.hasPermission;
  const hasActiveSubentry = hasActiveSubentryRecursive(section, activeEntry);
  const [collapsed, setCollapsed] = useState<boolean>(
    hasActiveSubentry ? false : section.collapsed ?? false
  );
  const isCollapsible = section.collapsed !== undefined;

  return (
    <Box
      sx={mixinSx(
        section.flatSection || section.isNavItem ? {} : { marginBottom: 1 },
        section.flatSection ? { marginTop: 1 } : {}
      )}
    >
      <Box
        sx={
          section.onAdd || isCollapsible
            ? {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }
            : undefined
        }
      >
        <LinkComponent
          section={section}
          hasPermission={showSubentries}
          activeEntry={activeEntry}
          sx={sx}
          appearsCollapsed={appearsCollapsed}
          isNavItem={section.isNavItem}
        >
          {section.icon && (
            <Box
              sx={{
                ml: 0.5,
                mr: 1,
                height: 24,
                // Prevents icon from causing weird looking focus outline due to too-tall icon
                display: 'flex',
                transition: transition('color'),
                alignItems: 'center',
              }}
            >
              <section.icon />
            </Box>
          )}
          <Box
            data-test={section.dataTest}
            data-appears-collapsed={appearsCollapsed}
            component="span"
            sx={{
              whiteSpace: 'nowrap',
              transition: transition('opacity'),
              opacity: 1,
              color: section.flatSection
                ? (theme) => theme.palette.grey50
                : undefined,
              '&[data-appears-collapsed="true"]': {
                opacity: 0,
              },
            }}
          >
            {section.name}
          </Box>
          <NavChip
            sx={{ marginLeft: 'auto' }}
            chip={section.chip}
            chipTooltip={section.chipTooltip}
          />
        </LinkComponent>
        {section.onAdd ? (
          <BoxLink
            data-test={section.onAddDataTest}
            onClick={section.onAdd}
            sx={{
              background: 'none',
              border: 'none',
              paddingX: '10px',
              paddingY: 0,
              cursor: 'pointer',
            }}
          >
            <AddIcon color="white" sx={{ verticalAlign: 'middle' }} />
          </BoxLink>
        ) : null}
        {isCollapsible && (
          <BoxLink
            onClick={() => setCollapsed(!collapsed)}
            sx={{
              background: 'none',
              border: 'none',
              paddingX: '10px',
              paddingY: 0,
              cursor: 'pointer',
            }}
          >
            {collapsed ? (
              <ChevronDownIcon color="white" />
            ) : (
              <ChevronUpIcon color="white" />
            )}
          </BoxLink>
        )}
      </Box>

      {showSubentries && !collapsed && (
        <NavSectionSubentries
          subentries={section.subentries}
          appearsCollapsed={appearsCollapsed}
          activeEntry={activeEntry}
        />
      )}
    </Box>
  );
}

function NavSectionSubentries({
  subentries,
  appearsCollapsed,
  activeEntry,
  isLevelTwo,
}: {
  subentries: Array<NavItemData>;
  appearsCollapsed: boolean;
  activeEntry: NavSectionData | NavItemData | null;

  // Yes, this prop is stupid, but I hear we're refactoring Sidebar soon,
  // so it's probably fine. If you are reading this comment in 2029,
  // just know that I meant well…
  isLevelTwo?: boolean;
}) {
  const sx = useSx();
  const listRef = useRef<HTMLUListElement>(null);
  const [listHeight, setListHeight] = useState<number>(0);
  // Cause to re-measure when length of nav entries changes.
  useEffect(() => {
    setListHeight(0);
  }, [subentries.length]);

  useLayoutEffect(() => {
    if (listRef.current && listHeight === 0) {
      setListHeight(listRef.current.getBoundingClientRect().height);
    }
  }, [listHeight, appearsCollapsed]);

  return (
    <Box
      component="ul"
      data-appears-collapsed={appearsCollapsed}
      ref={listRef}
      sx={{
        listStyleType: 'none',
        padding: 0,
        margin: 0,
        transition: transition('opacity', 'height'),
        height: listHeight || 'auto',
        '&[data-appears-collapsed="true"]': {
          height: 0,
          opacity: 0,
          // Necessary because when the SideBar width shrinks, the list wraps
          // and can appear above the navItemMain links.
          pointerEvents: 'none',
        },
      }}
    >
      {subentries.map((subentry) => {
        const lackingAccess = !subentry.hasPermission;
        const isActive = isActiveNavLink(subentry, activeEntry);
        return (
          <li key={subentry.objectId ?? subentry.name}>
            {subentry.disabled ? (
              <Box
                component="span"
                sx={mixinSx(sx.navItem, isLevelTwo && sx.navItemLevelTwo)}
                aria-disabled
              >
                {subentry.name}
              </Box>
            ) : lackingAccess ? (
              <Tooltip
                title={`Your permissions don’t allow for accessing ${smartLowerCase(
                  subentry.name
                )}.`}
                placement="right"
                sx={sx.permissionsTooltip}
              >
                <Box
                  component="span"
                  sx={mixinSx(sx.navItem, isLevelTwo && sx.navItemLevelTwo)}
                  aria-disabled
                >
                  {subentry.name}
                </Box>
              </Tooltip>
            ) : (
              <>
                <Tooltip title={subentry.tooltip || ''} placement="right-start">
                  <BoxLink
                    sx={mixinSx(
                      sx.navItem,
                      isActive && sx.navItemActive,
                      isLevelTwo && sx.navItemLevelTwo
                    )}
                    href={subentry.location}
                    onClick={() => {
                      Analytics.action('navBarMenuItem', {
                        label: subentry.name,
                        to: subentry.location,
                      });
                    }}
                    tabIndex={appearsCollapsed ? -1 : 0}
                    newTab={subentry.openInNewTab}
                    data-test={`button-${subentry.name}`}
                  >
                    <span>{subentry.name}</span>
                    <NavChip
                      chip={subentry.chip}
                      chipTooltip={subentry.chipTooltip}
                      isActive={isActive}
                    />
                  </BoxLink>
                </Tooltip>
                {subentry.subentries && (
                  <NavSectionSubentries
                    subentries={subentry.subentries}
                    appearsCollapsed={appearsCollapsed}
                    activeEntry={activeEntry}
                    isLevelTwo
                  />
                )}
              </>
            )}
          </li>
        );
      })}
    </Box>
  );
}
